// CAMINHO: src/components/cases/CaseProcessTimeline.tsx
import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar, FileText, Clock, RefreshCcw, PlusCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';

type TimelineEntryType = 'document' | 'movement' | 'note' | 'deadline' | 'hearing' | 'decision' | 'other';

interface TimelineEntry {
  id: string;
  title: string;
  description?: string;
  date: string; // ISO
  type: TimelineEntryType;
  user_id?: string;
  metadata?: any;
}

interface CaseProcessTimelineProps {
  caseId: string;
  onAddDocumentClick?: () => void;
}

// Pequeno helper para cor e rótulo por tipo
const typeInfo = (type: TimelineEntryType) => {
  switch (type) {
    case 'document': return { label: 'Documento', color: 'bg-amber-100 text-amber-700 border-amber-200' };
    case 'movement': return { label: 'Andamento', color: 'bg-blue-100 text-blue-700 border-blue-200' };
    case 'hearing': return { label: 'Audiência', color: 'bg-purple-100 text-purple-700 border-purple-200' };
    case 'deadline': return { label: 'Prazo', color: 'bg-red-100 text-red-700 border-red-200' };
    case 'decision': return { label: 'Decisão', color: 'bg-green-100 text-green-700 border-green-200' };
    case 'note': return { label: 'Nota', color: 'bg-gray-100 text-gray-700 border-gray-200' };
    default: return { label: 'Evento', color: 'bg-muted text-muted-foreground' };
  }
};

export const CaseProcessTimeline = ({ caseId, onAddDocumentClick }: CaseProcessTimelineProps) => {
  const { toast } = useToast();
  const [entries, setEntries] = useState<TimelineEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshKey, setRefreshKey] = useState<number>(0);

  const fetchTimeline = useCallback(async () => {
    setLoading(true);
    try {
      const results: TimelineEntry[] = [];

      // 1) Timeline explícita (case_timeline_entries)
      // Tenta ordenar por "entry_date" (schema atual). Se não existir, cai para "created_at".
      let explicitTimeline: any[] = [];
      try {
        const { data, error } = await (supabase as any)
          .from('case_timeline_entries')
          .select('*')
          .eq('case_id', caseId)
          .order('entry_date', { ascending: false });
        if (error) throw error;
        explicitTimeline = data || [];
      } catch (err: any) {
        const { data, error } = await (supabase as any)
          .from('case_timeline_entries')
          .select('*')
          .eq('case_id', caseId)
          .order('created_at', { ascending: false });
        if (error) throw error;
        explicitTimeline = data || [];
      }
      (explicitTimeline || []).forEach((item: any) => {
        results.push({
          id: item.id,
          title: item.title,
          description: item.description || undefined,
          date: item.entry_date || item.date || item.created_at,
          type: (item.type || item.entry_type || 'other') as TimelineEntryType,
          user_id: item.user_id,
          metadata: item.details || item.metadata,
        });
      });

      // 2) Documentos (case_documents) - garante que documentos antigos apareçam
      const { data: docs, error: docsError } = await (supabase as any)
        .from('case_documents')
        .select('*')
        .eq('case_id', caseId)
        .order('created_at', { ascending: false });
      if (docsError) throw docsError;
      (docs || []).forEach((doc: any) => {
        // Evitar duplicar se já houver timeline explícita com metadata.document_id
        const already = results.some(r => r.type === 'document' && r.metadata?.document_id === doc.id);
        if (!already) {
          results.push({
            id: `doc-${doc.id}`,
            title: doc.file_name,
            description: undefined,
            date: doc.created_at,
            type: 'document',
            user_id: doc.user_id,
            metadata: { document_id: doc.id, file_path: doc.file_path, file_size: doc.file_size },
          });
        }
      });

      // 3) Movimentos (case_movements) se existir
      const { data: movements, error: moveError } = await (supabase as any)
        .from('case_movements')
        .select('*')
        .eq('case_id', caseId)
        .order('date', { ascending: false });
      if (!moveError && movements) {
        movements.forEach((mv: any) => {
          results.push({
            id: `mv-${mv.id}`,
            title: mv.movement_type || 'Movimento',
            description: mv.description || undefined,
            date: mv.date || mv.created_at,
            type: 'movement',
          });
        });
      }

      // Ordenar por data desc
      results.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setEntries(results);
    } catch (err: any) {
      console.error('Erro ao carregar timeline:', err);
      toast({ title: 'Erro ao carregar timeline', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [caseId, toast]);

  useEffect(() => {
    fetchTimeline();
  }, [fetchTimeline, refreshKey]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Histórico do Processo
          </CardTitle>
          <div className="flex gap-2">
            {onAddDocumentClick && (
              <Button variant="default" size="sm" onClick={onAddDocumentClick}>
                <PlusCircle className="w-4 h-4 mr-2" />
                Adicionar Documento
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setRefreshKey(k => k + 1)}>
              <RefreshCcw className="w-4 h-4 mr-2" />
              Atualizar
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground"><Clock className="w-4 h-4" />Carregando timeline...</div>
        ) : entries.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Calendar className="w-10 h-10 mx-auto mb-2 opacity-60" />
            Nenhum evento registrado ainda.
          </div>
        ) : (
          <div className="relative">
            {/* Linha base */}
            <div className="absolute left-0 right-0 top-10 h-0.5 bg-border"></div>

            {/* Container horizontal scroll */}
            <div className="overflow-x-auto no-scrollbar">
              <div className="flex items-start gap-6 min-w-full py-6">
                {entries.map((entry) => {
                  const info = typeInfo(entry.type);
                  return (
                    <div key={entry.id} className="min-w-[260px]">
                      {/* Marcador */}
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center">
                          {entry.type === 'document' ? (
                            <FileText className="w-5 h-5 text-primary" />
                          ) : (
                            <Calendar className="w-5 h-5 text-primary" />
                          )}
                        </div>
                      </div>
                      {/* Cartão do item */}
                      <div className="mt-4 bg-muted/40 border rounded-lg p-4 shadow-sm">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium truncate max-w-[160px]" title={entry.title}>{entry.title}</h4>
                          <Badge className={`border ${info.color}`}>{info.label}</Badge>
                        </div>
                        {entry.description && (
                          <p className="text-sm text-muted-foreground mb-2 line-clamp-3">{entry.description}</p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(entry.date), 'dd/MM/yyyy HH:mm')}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};